import * as React from 'react'
import { FormField } from '@/components/templates/entity-form'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

export interface DonationFormValues {
  campaignId: string
}
export interface DonationTributeValues {
  tributeType: 'honor' | 'memory' | null
  honoreeName: string
  displayAnonymous: boolean
}
export interface Campaign {
  id: number
  name: string
}

export function CampaignField<T extends DonationFormValues>({
  campaigns,
  disabled,
  form,
  setForm,
}: {
  campaigns: Campaign[]
  disabled: boolean
  form: T
  setForm: React.Dispatch<React.SetStateAction<T>>
}) {
  return (
    <FormField label="Campaign">
      <Select
        value={form.campaignId || 'general'}
        onValueChange={(campaignId) =>
          setForm({ ...form, campaignId: campaignId === 'general' ? '' : campaignId })
        }
        disabled={disabled}
      >
        <SelectTrigger aria-label="Campaign" className="h-8">
          <SelectValue placeholder="General" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="general">General</SelectItem>
          {campaigns.map((campaign) => (
            <SelectItem key={campaign.id} value={String(campaign.id)}>
              {campaign.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </FormField>
  )
}

export function DonationTributeFields<T extends DonationTributeValues>({
  form,
  setForm,
}: {
  form: T
  setForm: React.Dispatch<React.SetStateAction<T>>
}) {
  if (!form.tributeType) {
    return (
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="justify-self-start border-dashed sm:col-span-2"
        onClick={() => setForm({ ...form, tributeType: 'honor' })}
      >
        + Add tribute
      </Button>
    )
  }

  return (
    <div className="grid gap-3 border-t border-border pt-3 sm:col-span-2 sm:grid-cols-2">
      <FormField label="Tribute type">
        <Select
          value={form.tributeType}
          onValueChange={(tributeType: 'honor' | 'memory') => setForm({ ...form, tributeType })}
        >
          <SelectTrigger aria-label="Tribute type" className="h-8">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="honor">In honor of</SelectItem>
            <SelectItem value="memory">In memory of</SelectItem>
          </SelectContent>
        </Select>
      </FormField>
      <FormField label="Honoree name" htmlFor="donation-honoree">
        <Input
          id="donation-honoree"
          className="h-8"
          value={form.honoreeName}
          onChange={(event) => setForm({ ...form, honoreeName: event.target.value })}
        />
      </FormField>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="justify-self-start text-caption"
        onClick={() =>
          setForm({ ...form, tributeType: null, honoreeName: '', displayAnonymous: false })
        }
      >
        Clear tribute
      </Button>
      <label
        htmlFor="donation-display-anonymous"
        className="flex items-center gap-2 text-body-sm sm:col-span-2"
      >
        <Checkbox
          id="donation-display-anonymous"
          checked={form.displayAnonymous}
          onCheckedChange={(checked) => setForm({ ...form, displayAnonymous: checked === true })}
        />
        <span>Display as Anonymous</span>
      </label>
    </div>
  )
}
